import { useState } from 'react';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { useHasPermission } from '@/auth/auth-store';
import {
  CONFIG_NAMESPACES,
  useUpdateProfileConfig,
  type ConfigNamespace,
  type TerminalConfigValue,
  type TerminalProfile,
} from '@/hooks/use-terminals';
import {
  NamespaceFields,
  NAMESPACE_DEFAULTS,
  namespaceLabel,
  summariseNamespace,
} from './terminal-config-fields';
import { useT } from '@/i18n/use-t';

/** The shape `useT` returns, for the helper below that cannot call a hook. */
type Translate = (key: string, bakedEnglish?: string) => string;

/**
 * RFL-241 — the GROUP level of terminal configuration.
 *
 * A profile's config is the default every terminal bound to it receives, so
 * editing it here changes what a set of tills is delivered on their next sync.
 * The per-terminal override lives on the terminal detail page.
 *
 * Two namespaces here, one namespace there is not a choice the data model
 * makes: the same three namespaces exist at both levels, which is why the
 * field editors are shared rather than reimplemented per level.
 */
export function ProfileConfigEditor({ profile }: { profile: TerminalProfile }) {
  const t = useT('admin_ui');
  const canAdmin = useHasPermission('terminal-management.admin');
  const update = useUpdateProfileConfig();
  const [draft, setDraft] = useState<TerminalConfigValue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const config = draft ?? profile.config ?? {};
  const dirty = draft !== null;
  const configured = CONFIG_NAMESPACES.filter((ns) => config[ns] !== undefined);

  const toggle = (ns: ConfigNamespace, on: boolean) => {
    const copy: TerminalConfigValue = { ...config };
    if (on) copy[ns] = NAMESPACE_DEFAULTS[ns] as never;
    else delete copy[ns];
    setDraft(copy);
  };

  const summary =
    configured.length === 0
      ? t('terminals.profileConfig.notConfigured', 'Not configured')
      : configured.map((ns) => namespaceLabel(ns, t)).join(', ');

  if (!open || !canAdmin) {
    if (!canAdmin) {
      return <span className="text-xs text-muted-foreground">{summary}</span>;
    }
    return (
      <button
        type="button"
        className="text-left text-xs text-muted-foreground underline-offset-2 hover:underline"
        onClick={() => setOpen(true)}
      >
        {summary} — {t('terminals.profileConfig.edit', 'edit')}
      </button>
    );
  }

  return (
    <div className="space-y-4 rounded-md border bg-background p-4">
      {error !== null && <p className="text-sm text-destructive">{error}</p>}
      {CONFIG_NAMESPACES.map((ns) => {
        const value = config[ns];
        return (
          <section key={ns}>
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={value !== undefined}
                onChange={(e) => toggle(ns, e.target.checked)}
              />
              {namespaceLabel(ns, t)}
              <span className="font-normal text-xs text-muted-foreground">
                {summariseNamespace(ns, value, t)}
              </span>
            </label>
            {value !== undefined && (
              <div className="mt-2 pl-6">
                <NamespaceFields
                  namespace={ns}
                  value={value}
                  onChange={(next) => setDraft({ ...config, [ns]: next })}
                />
              </div>
            )}
          </section>
        );
      })}

      <div className="flex items-center gap-2">
        <Button
          disabled={!dirty || update.isPending}
          onClick={async () => {
            setError(null);
            try {
              // An empty bag is sent as null: "this profile configures nothing"
              // is the absence of config, and `{}` would read as configured.
              await update.mutateAsync({
                id: profile.id,
                config: Object.keys(config).length === 0 ? null : config,
                expectedConfigVersion: profile.configVersion,
              });
              setDraft(null);
              setOpen(false);
            } catch (err) {
              setError(extractError(err, t));
            }
          }}
        >
          {update.isPending ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setDraft(null);
            setError(null);
            setOpen(false);
          }}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
      </div>
    </div>
  );
}

/** The server's message names the offending field; keep it. */
function extractError(err: unknown, t: Translate): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { message?: string | string[] } | undefined;
    const message = data?.message;
    if (Array.isArray(message)) return message.join('; ');
    if (typeof message === 'string') return message;
    return err.message;
  }
  return t('terminals.error.unexpected', 'Unexpected error');
}
